import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { downloadDocument, downloadRecuFTPE, downloadRecuESS, viewDocument } from '../services/downloadService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useMesDocumentsMembre(membreId) {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [documentsGed, setDocumentsGed] = useState([]);
  const [souscriptionsEnCours, setSouscriptionsEnCours] = useState([]);
  const [recusPaiements, setRecusPaiements] = useState([]);
  const [documentsAdmin, setDocumentsAdmin] = useState([]);
  const [categories, setCategories] = useState({});
  const [hasFtpeSignee, setHasFtpeSignee] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const targetId = membreId || user?.id || user?.membre_id;
      if (!targetId) {
        setDocuments([]);
        return;
      }
      const response = await axios.get(`${API}/cooper/membres/${targetId}/mes-documents`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        const data = response.data;
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const hasValidUrl = (doc) => {
          const url = doc.url || doc.download_url || doc.file_url || doc.path;
          return url && typeof url === 'string' && url.trim().length > 0;
        };

        const isWithin30Days = (doc) => {
          const createdAt = doc.date_creation || doc.created_at;
          if (!createdAt) return true;
          try {
            const createdDt = new Date(createdAt);
            return createdDt >= thirtyDaysAgo;
          } catch {
            return true;
          }
        };

        // Deduplication: versions admin signees remplacent les versions systeme
        const adminDocs = (data.documents_admin || []);
        const adminTypes = new Set(adminDocs.map(d => d.type));
        const ADMIN_SUPERSEDES = {
          'attestation_signee': ['attestation_rcds'],
          'conditions_generales_particulieres_signees': ['conditions_generales'],
          'attestation_ftpe_signee': ['attestation_ftpe'],
          'devis_ess': ['devis_ess'],
        };
        const supersededTypes = new Set();
        for (const [adminType, sysTypes] of Object.entries(ADMIN_SUPERSEDES)) {
          if (adminTypes.has(adminType)) sysTypes.forEach(t => supersededTypes.add(t));
        }
        const allDocs = (data.documents || []).filter(d => {
          if (supersededTypes.has(d.type)) return false;
          if (d.type === 'attestation_ftpe') return true;
          return hasValidUrl(d);
        });

        const simulations = (data.souscriptions_en_cours || []).filter(sim => {
          return isWithin30Days(sim) && sim.jours_restants > 0;
        });

        const gedValides = (data.documents_ged || []).filter(hasValidUrl);
        const recusValides = (data.recus_paiements || []).filter(hasValidUrl);

        setDocuments(allDocs);
        setDocumentsAdmin(adminDocs);
        setDocumentsGed(gedValides);
        setSouscriptionsEnCours(simulations);
        setRecusPaiements(recusValides);
        setCategories(data.categories || {});
        setHasFtpeSignee(data.has_ftpe_signee || allDocs.some(d => d.type === 'attestation_ftpe' && d.document_status === 'signee'));
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Erreur lors du chargement des documents');
      }
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [membreId, user, getAuthHeader]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      await downloadDocument(doc);
      toast.success('Telechargement termine');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (doc) => {
    try {
      await viewDocument(doc);
    } catch (error) {
      console.error('View error:', error);
      toast.error('Erreur lors de la visualisation');
    }
  };

  const handleDownloadRecu = async (recu) => {
    setDownloadingId(recu.id);
    try {
      if (recu.type === 'recu_paiement_ftpe' && recu.adhesion_id) {
        await downloadRecuFTPE(recu.adhesion_id);
      } else if (recu.type === 'recu_ess' && recu.recu_id) {
        await downloadRecuESS(recu.recu_id);
      } else {
        await downloadDocument(recu);
      }
      toast.success('Telechargement termine');
    } catch (error) {
      console.error('Recu download error:', error);
      toast.error('Erreur lors du telechargement du recu');
    } finally {
      setDownloadingId(null);
    }
  };

  // Deduplication intelligente des attestations FTPE
  const deduplicatedDocuments = (() => {
    const ftpeDocs = documents.filter(d => d.type === 'attestation_ftpe');
    let otherDocs = documents.filter(d => d.type !== 'attestation_ftpe');
    if (!hasFtpeSignee) {
      otherDocs = otherDocs.filter(d => d.type !== 'certificat_adhesion');
    }
    const byAdhesion = {};
    ftpeDocs.forEach(doc => {
      const key = doc.adhesion_id || doc.id;
      if (!byAdhesion[key]) byAdhesion[key] = [];
      byAdhesion[key].push(doc);
    });
    const bestFtpeDocs = [];
    Object.values(byAdhesion).forEach(group => {
      const signee = group.find(d => d.document_status === 'signee');
      if (signee) {
        bestFtpeDocs.push(signee);
      } else {
        bestFtpeDocs.push(group[0]);
      }
    });
    return [...bestFtpeDocs, ...otherDocs];
  })();

  const filteredDocuments = activeFilter === 'all'
    ? deduplicatedDocuments
    : deduplicatedDocuments.filter(doc => {
        if (activeFilter === 'devis_ess') {
          return doc.type === 'devis_ess' || doc.type === 'devis_ess_genere';
        }
        return doc.type === activeFilter;
      });

  return {
    navigate, documents, documentsGed, souscriptionsEnCours, recusPaiements, documentsAdmin,
    categories, hasFtpeSignee, loading, downloadingId, activeFilter, setActiveFilter,
    fetchDocuments, handleDownload, handleView, handleDownloadRecu,
    deduplicatedDocuments, filteredDocuments,
  };
}
